import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Award, TrendingUp, Users, Shield, CheckCircle, Sparkles } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';
import SEO from '../components/SEO';

const Home = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 300]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const heroLines = ['Empowering Growth &', 'Building Success'];

  const lineVariants = {
    hidden: { opacity: 0, y: 100 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.3 + 0.5,
        duration: 1,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    })
  };

  const subtitleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        delay: 1.4,
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };

  const ctaVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        delay: 1.8,
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  const services = [
    {
      number: '01',
      title: 'Food Industry Consultancy',
      description: 'Expert guidance in FSSC 22000, HACCP, supplier audits, and regulatory compliance for food businesses.',
      icon: Shield
    },
    {
      number: '02',
      title: 'Financial Consultancy',
      description: 'Company incorporation, tax filing, bookkeeping, and investment planning for sustainable growth.',
      icon: TrendingUp
    },
    {
      number: '03',
      title: 'Business Strategy',
      description: 'Tailored solutions for operational efficiency, market development, and long-term success.',
      icon: Award
    }
  ];

  const manifesto = [
    {
      number: '01',
      title: 'Industry Expertise',
      text: '20+ years of international experience across food safety, quality management, and financial services.'
    },
    {
      number: '02',
      title: 'Personalized Solutions',
      text: 'Tailored consulting that meets your unique goals, not one-size-fits-all advice.'
    },
    {
      number: '03',
      title: 'Results-Driven',
      text: 'We work as trusted partners to deliver practical, measurable outcomes for sustainable growth.'
    },
    {
      number: '04',
      title: 'Long-Term Commitment',
      text: 'Building lasting partnerships based on trust, professionalism, and real value creation.'
    }
  ];

  const stats = [
    { value: '20+', label: 'Years Experience' },
    { value: '100+', label: 'Clients Served' },
    { value: '98%', label: 'Success Rate' }
  ];

  return (
    <div className="home-page">
      <SEO
        title=""
        path="/"
        description="Prime Food & Financial Consultancy in the Netherlands. Expert food safety (FSSC 22000, HACCP, ISO 22000), regulatory compliance, and financial consultancy including company incorporation, tax, bookkeeping & investment planning."
        keywords="Prime Food Financial Consultancy, food safety consultancy Netherlands, FSSC 22000, HACCP, ISO 22000, financial consultancy, company incorporation Netherlands, food consultancy"
      />
      <motion.section
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{ y: heroY, opacity: heroOpacity }}
      >
        <div className="absolute inset-0 z-0">
          <motion.div
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: 'easeOut' }}
            className="w-full h-full"
          >
            <img
              src="https://images.unsplash.com/photo-1581090698407-7d93959da202"
              alt="Professional Consultancy"
              className="w-full h-full object-cover"
            />
            <div
              className="absolute inset-0 bg-gradient-to-r from-navy-900/95 via-navy-900/85 to-transparent"
              style={{ backgroundColor: 'rgba(10, 24, 40, 0.85)' }}
            />
          </motion.div>
        </div>

        <div className="container relative z-10 py-32">
          <div className="max-w-4xl">
            <div className="overflow-hidden mb-6">
              {heroLines.map((line, i) => (
                <div key={i} className="overflow-hidden">
                  <motion.h1
                    custom={i}
                    initial="hidden"
                    animate={isLoaded ? 'visible' : 'hidden'}
                    variants={lineVariants}
                    className="font-serif font-bold text-[24px] sm:text-[29px] md:text-[48px] lg:text-[58px] mb-2 break-words"
                    style={{ color: 'var(--color-cream)' }}
                  >
                    {line}
                  </motion.h1>
                </div>
              ))}
            </div>

            <motion.p
              initial="hidden"
              animate={isLoaded ? 'visible' : 'hidden'}
              variants={subtitleVariants}
              className="text-xl md:text-2xl mb-8 leading-relaxed max-w-2xl"
              style={{ color: 'var(--color-cream)', opacity: 0.9 }}
            >
              Welcome to Prime Food &amp; Financial Consultancy. Expert solutions for food businesses and financial growth in the Netherlands and international market.
            </motion.p>

            <motion.div
              initial="hidden"
              animate={isLoaded ? 'visible' : 'hidden'}
              variants={ctaVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05, x: 5 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary flex items-center gap-2 group"
                >
                  <span>Explore Services</span>
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </Link>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-secondary"
                  style={{ borderColor: 'var(--color-cream)', color: 'var(--color-cream)' }}
                >
                  <span>Schedule Consultation</span>
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 0.8 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 border-2 rounded-full flex justify-center"
            style={{ borderColor: 'var(--color-cream)' }}
          >
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1 h-3 rounded-full mt-2"
              style={{ backgroundColor: 'var(--color-cream)' }}
            />
          </motion.div>
        </motion.div>
      </motion.section>

      <AnimatedSection>
        <div className="py-8 overflow-hidden" style={{ backgroundColor: 'var(--color-emerald)' }}>
          <div className="flex whitespace-nowrap marquee">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center space-x-12 mr-12">
                <span className="text-white font-medium text-lg flex items-center gap-2">
                  <Sparkles size={20} /> FSSC 22000 Certified
                </span>
                <span className="text-white font-medium text-lg flex items-center gap-2">
                  <Sparkles size={20} /> ISO 22000 Expert
                </span>
                <span className="text-white font-medium text-lg flex items-center gap-2">
                  <Sparkles size={20} /> 20+ Years Experience
                </span>
                <span className="text-white font-medium text-lg flex items-center gap-2">
                  <Sparkles size={20} /> International Expertise
                </span>
                <span className="text-white font-medium text-lg flex items-center gap-2">
                  <Sparkles size={20} /> Trusted Partner
                </span>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      <section className="section" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div>
                <motion.div
                  whileInView={{ scale: [0.8, 1.05, 1] }}
                  transition={{ duration: 0.6 }}
                  className="inline-block mb-4 px-4 py-2 rounded-full"
                  style={{ backgroundColor: 'var(--color-emerald)', color: 'white' }}
                >
                  <span className="text-sm font-semibold tracking-wide">OUR PURPOSE</span>
                </motion.div>
                <h2 className="font-serif text-5xl font-bold mb-6" style={{ color: 'var(--color-navy)' }}>
                  Our Mission
                </h2>
                <p className="text-lg leading-relaxed mb-6" style={{ color: 'var(--color-dark-gray)' }}>
                  Our mission is simple: to empower businesses and individuals with the knowledge, strategies, and confidence needed to achieve sustainable growth and long-term success.
                </p>
                <p className="text-lg leading-relaxed" style={{ color: 'var(--color-dark-gray)' }}>
                  We work as trusted partners, delivering tailored solutions that meet your unique goals rather than offering one-size-fits-all advice.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative h-96 rounded-none overflow-hidden shadow-2xl"
              >
                <img
                  src="https://images.pexels.com/photos/7181178/pexels-photo-7181178.jpeg"
                  alt="Team Collaboration"
                  className="w-full h-full object-cover"
                />
                <div
                  className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent"
                  style={{ background: 'linear-gradient(to top, rgba(10, 24, 40, 0.5), transparent)' }}
                />
              </motion.div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <AnimatedSection>
        <section className="py-20" style={{ backgroundColor: 'var(--color-navy)' }}>
          <div className="container">
            <div className="grid grid-cols-3 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="text-center"
                >
                  <div className="font-serif text-5xl font-bold mb-2" style={{ color: 'var(--color-gold)' }}>
                    {stat.value}
                  </div>
                  <div className="text-sm uppercase tracking-wider" style={{ color: 'var(--color-cream)', opacity: 0.8 }}>
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      <section className="section" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="container">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="font-serif text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--color-navy)' }}>
                What We Do
              </h2>
              <p className="text-xl leading-relaxed" style={{ color: 'var(--color-dark-gray)' }}>
                Comprehensive consultancy services combining food industry expertise with financial insight.
              </p>
            </div>
          </AnimatedSection>

          <div className="space-y-12">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <AnimatedSection key={index} delay={index * 0.2}>
                  <motion.div
                    whileHover={{ x: 10 }}
                    className="group grid md:grid-cols-12 gap-8 items-start py-12 border-b"
                    style={{ borderColor: 'var(--color-emerald)' }}
                  >
                    <div className="md:col-span-2">
                      <div className="font-serif text-6xl font-bold opacity-20" style={{ color: 'var(--color-emerald)' }}>
                        {service.number}
                      </div>
                    </div>
                    <div className="md:col-span-8">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="p-3 rounded-full" style={{ backgroundColor: 'var(--color-emerald)' }}>
                          <Icon size={24} style={{ color: 'white' }} />
                        </div>
                        <h3 className="font-serif text-3xl font-semibold" style={{ color: 'var(--color-navy)' }}>
                          {service.title}
                        </h3>
                      </div>
                      <p className="text-lg leading-relaxed" style={{ color: 'var(--color-dark-gray)' }}>
                        {service.description}
                      </p>
                    </div>
                    <div className="md:col-span-2 flex md:justify-end items-center">
                      <motion.div
                        whileHover={{ x: 5 }}
                        className="group-hover:opacity-100 opacity-60 transition-opacity"
                      >
                        <ArrowRight size={32} style={{ color: 'var(--color-emerald)' }} />
                      </motion.div>
                    </div>
                  </motion.div>
                </AnimatedSection>
              );
            })}
          </div>

          <AnimatedSection delay={0.6}>
            <div className="text-center mt-16">
              <Link to="/services">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary"
                >
                  <span>View All Services</span>
                </motion.button>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="section" style={{ backgroundColor: 'var(--color-navy)', color: 'var(--color-cream)' }}>
        <div className="container">
          <AnimatedSection>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="font-serif text-5xl md:text-6xl font-bold mb-6" style={{ color: 'var(--color-gold)' }}>
                Why Choose Us
              </h2>
              <p className="text-xl leading-relaxed opacity-90">
                Our commitment to excellence, delivered through four core principles.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-12">
            {manifesto.map((item, index) => (
              <AnimatedSection key={index} delay={index * 0.15}>
                <motion.div
                  whileHover={{ y: -10 }}
                  className="p-8 border-l-4"
                  style={{ borderColor: 'var(--color-gold)' }}
                >
                  <div className="flex items-start gap-6">
                    <div className="font-serif text-5xl font-bold opacity-30" style={{ color: 'var(--color-gold)' }}>
                      {item.number}
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl font-semibold mb-3" style={{ color: 'var(--color-gold)' }}>
                        {item.title}
                      </h3>
                      <p className="text-lg leading-relaxed opacity-80">
                        {item.text}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ backgroundColor: 'var(--color-cream)' }}>
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative h-96 rounded-none overflow-hidden shadow-2xl order-2 lg:order-1"
              >
                <img
                  src="https://images.pexels.com/photos/7876501/pexels-photo-7876501.jpeg"
                  alt="Financial Planning"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="order-1 lg:order-2">
                <motion.div
                  whileInView={{ scale: [0.8, 1.05, 1] }}
                  transition={{ duration: 0.6 }}
                  className="inline-block mb-4 px-4 py-2 rounded-full"
                  style={{ backgroundColor: 'var(--color-gold)', color: 'var(--color-navy)' }}
                >
                  <span className="text-sm font-semibold tracking-wide">OUR VISION</span>
                </motion.div>
                <h2 className="font-serif text-5xl font-bold mb-6" style={{ color: 'var(--color-navy)' }}>
                  Building the Future
                </h2>
                <p className="text-lg leading-relaxed mb-6" style={{ color: 'var(--color-dark-gray)' }}>
                  We aspire to become one of the Netherlands most trusted consultancy firms, recognized for helping businesses thrive, empowering entrepreneurs, and enabling individuals to achieve financial confidence and lasting success.
                </p>
                <div className="space-y-4">
                  {['Sustainable Growth', 'Trusted Partnerships', 'Measurable Results'].map((item, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.1, duration: 0.5 }}
                      className="flex items-center gap-3"
                    >
                      <CheckCircle size={24} style={{ color: 'var(--color-emerald)' }} />
                      <span className="text-lg font-medium" style={{ color: 'var(--color-navy)' }}>{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <AnimatedSection>
        <section className="py-24" style={{ backgroundColor: 'var(--color-emerald)' }}>
          <div className="container text-center">
            <motion.h2
              whileInView={{ scale: [0.9, 1] }}
              className="font-serif text-5xl md:text-6xl font-bold mb-6 text-white"
            >
              Let&#39;s Build Your Future Together
            </motion.h2>
            <motion.p
              whileInView={{ opacity: [0, 1] }}
              transition={{ delay: 0.2 }}
              className="text-xl mb-10 text-white/90 max-w-3xl mx-auto leading-relaxed"
            >
              Whether you are an entrepreneur with a new idea, an established food business seeking growth, or an individual looking for financial guidance, we&#39;re ready to help you move forward with confidence.
            </motion.p>
            <motion.div
              whileInView={{ y: [30, 0], opacity: [0, 1] }}
              transition={{ delay: 0.4 }}
            >
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn-primary"
                  style={{ backgroundColor: 'var(--color-navy)' }}
                >
                  <span>Get Started Today</span>
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default Home;
